import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  styled,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ButtonGroup,
  Modal,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import PrintIcon from '@mui/icons-material/Print';
import AccountGroupModal from './AccountGroupModal';
import AddAccount from './AddAccount';
import AccountDetail from './AccountDetail';
import PassiveAccounts from './PassiveAccounts';
import AccountTransactions from './AccountTransactions';
import AccountMovementsReport from './AccountMovementsReport';
import ShippingAddressModal from './ShippingAddressModal';
import AccountsTableSettings from './AccountsTableSettings';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CollectPaymentModal from './CollectPaymentModal';
import MakePaymentModal from './MakePaymentModal';
import DebitAccountModal from './DebitAccountModal';
import CashTransferModal from '../cashbox/CashTransferModal';
import { getDoc, getDocs, doc, collection, query, where, addDoc, updateDoc, deleteDoc, orderBy } from "firebase/firestore";
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import BulkAddAccountModal from './BulkAddAccountModal';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AccountingDataTable, { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Cari hesap arayüzü
interface Account {
  id: string;
  companyName: string;
  groupId: string | null;
  authorizedPerson: string;
  mobilePhone: string;
  taxNumber: string;
  accountCode: string;
  createdAt: string;
  balance?: number;
  debitBalance?: number;
  calculatedBalance?: number; // Hesaplanan bakiye
  branchId?: string | null;
  isActive?: boolean; // isActive özelliğini ekledim
  preventNegativeBalance?: boolean; // Yeni alan eklendi
  // B2B / e-ticaret alanları — düzenleme modalının prefill'i için listede taşınmalı
  riskLimitAmount?: number | null;
  riskLimitCurrency?: string | null;
  paymentDueDays?: number | null;
  riskLimitAction?: string | null;
  isEcommerceUser?: boolean;
  isB2BDealer?: boolean;
  ecommerceDiscountPercent?: number | null;
  ecommercePriceBasis?: string | null;
  blockOrdersWhenOverdue?: boolean;
}

// Cari hesap detayları arayüzü
interface AccountDetails extends Account {
  phone: string;
  email: string;
  website: string;
  postalCode: string;
  taxOffice: string;
  tradeRegisterNumber: string;
  country: string;
  city: string;
  district: string;
  address: string;
  notes: string;
  // preventNegativeBalance?: boolean; // Account'tan miras alındı, tekrar eklemeye gerek yok
}

// Cari grup arayüzü
interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  /**
   * SQL'deki sayısal grup ID'si (string olarak).
   * id alanı Firebase ID varsa onu taşıdığı için sunucu tarafı filtreleme ve
   * carinin sayısal groupId ile eşleşmesi bu alan üzerinden yapılır.
   */
  sqlId?: string | null;
}

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  sortable?: boolean;
  description?: string;
}

// LocalStorage key
const ACCOUNTS_TABLE_SETTINGS_KEY = 'accountsTableSettings';

// Modal stil tanımı
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

interface AccountsProps {
  /** Manager panelinden açıldığında true — şube filtresi kilitlenir */
  isManager?: boolean;
  /** Manager'ın aktif şubesi (Firebase branch ID) */
  managerBranchId?: string | null;
}

const Accounts: React.FC<AccountsProps> = ({ isManager = false, managerBranchId = null }) => {
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches, userDetails } = useAuth();
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';
  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    if (!/[ÃÂÅÄ]/.test(value)) return value;
    try {
      return decodeURIComponent(escape(value));
    } catch {
      return value;
    }
  };
  const isGeneralAccountName = (value?: string | null): boolean => decodeMojibake(value).trim() === 'Genel Cari';
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 🔍 OFFLINE DEBUG - Sadece development'ta ve mount'ta bir kez logla
  const hasLoggedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('🚀 [ACCOUNTS] Sayfa MOUNT edildi');
      console.log('🔍 [ACCOUNTS] AuthContext branches:', branches?.length || 0, 'adet');
      console.log('🔍 [ACCOUNTS] AuthContext userDetails:', userDetails ? `✅ companyId: ${userDetails.companyId}` : '❌ YOK');
    }
  }, []);
  
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null); // ✅ YENİ: Düzenlenecek cari
  const [isPassiveAccountsModalOpen, setIsPassiveAccountsModalOpen] = useState(false);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);
  // ✅ Manager (embedded) mod: raporlar admin rotası yerine modal içinde açılır
  const [managerReportAccountId, setManagerReportAccountId] = useState<string | null>(null);
  const [isManagerMovementsReportOpen, setIsManagerMovementsReportOpen] = useState(false);
  const [isShippingAddressModalOpen, setIsShippingAddressModalOpen] = useState(false);
  const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] = useState(false);
  const [isCollectPaymentModalOpen, setIsCollectPaymentModalOpen] = useState<boolean>(false);
  const [isMakePaymentModalOpen, setIsMakePaymentModalOpen] = useState<boolean>(false);
  const [isDebitModalOpen, setIsDebitModalOpen] = useState<boolean>(false);
  const [isCashTransferModalOpen, setIsCashTransferModalOpen] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountDetails | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [isAccountTransactionDrawerOpen, setIsAccountTransactionDrawerOpen] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [isBulkAddAccountModalOpen, setIsBulkAddAccountModalOpen] = useState(false);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<AccountGroup | null>(null);
  const allGroupsOption = useMemo(
    () => ({ id: 'all', sqlId: null, name: t('current.filters.allGroups'), parentId: null, level: 0 }),
    [t]
  );
  // ✅ Şube filtresi — manager için kilitli (kendi şubesi), admin için serbest
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string | null>(null);
  const effectiveBranchId = isManager ? (managerBranchId || null) : selectedBranchFilter;
  
  // İşlemler menüsü için state'ler
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAccountForMenu, setSelectedAccountForMenu] = useState<Account | null>(null);
  
  // Üst menü için 3 nokta menüsü
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  
  // Info modalı için state
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  
  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };
  
  // Tablo sütunları
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'rowNumber', label: t('current.table.columns.rowNumber'), field: 'rowNumber', visible: true, fixed: true, description: t('current.modals.tableSettings.descriptions.rowNumber') },
    { id: 'companyName', label: t('current.table.columns.companyName'), field: 'companyName', visible: true, fixed: true, sortable: true, description: t('current.modals.tableSettings.descriptions.companyName') },
    { id: 'group', label: t('current.table.columns.group'), field: 'groupId', visible: true, fixed: false, sortable: true, description: t('current.modals.tableSettings.descriptions.group') },
    { id: 'authorizedPerson', label: t('current.table.columns.authorizedPerson'), field: 'authorizedPerson', visible: true, fixed: false, sortable: true, description: t('current.modals.tableSettings.descriptions.authorizedPerson') },
    { id: 'mobilePhone', label: t('current.table.columns.mobilePhone'), field: 'mobilePhone', visible: false, fixed: false, sortable: false, description: t('current.modals.tableSettings.descriptions.mobilePhone') },
    { id: 'taxNumber', label: t('current.table.columns.taxNumber'), field: 'taxNumber', visible: true, fixed: false, sortable: false, description: t('current.modals.tableSettings.descriptions.taxNumber') },
    { id: 'branch', label: t('current.table.columns.branch'), field: 'branchId', visible: false, fixed: false, sortable: false, description: t('current.modals.tableSettings.descriptions.branch') },
    { id: 'balance', label: t('current.table.columns.balance'), field: 'balance', visible: true, fixed: true, sortable: true, description: t('current.modals.tableSettings.descriptions.balance') },
    { id: 'actions', label: t('current.table.columns.actions'), field: 'actions', visible: true, fixed: true, description: t('current.modals.tableSettings.descriptions.actions') },
  ]);
  
  // Mobil sayfalama için state → artık hem desktop hem mobile server-side
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileRowsPerPage, setMobileRowsPerPage] = useState(50);

  // ✅ Server-side pagination/sorting state
  const [serverPage, setServerPage] = useState(0); // 0-based
  const [serverRowsPerPage, setServerRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('companyName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [serverTotalCredit, setServerTotalCredit] = useState(0);
  const [serverTotalDebit, setServerTotalDebit] = useState(0);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchIdRef = useRef(0); // Race condition koruması

  const navigate = useNavigate();

  // ✅ Arama debounce (400ms) — kullanıcı yazmayı bıraktıktan sonra API'ye istek gönder
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setServerPage(0); // Arama değiştiğinde ilk sayfaya dön
      setMobilePage(0);
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // LocalStorage'dan tablo ayarlarını yükle
  useEffect(() => {
    const savedSettings = localStorage.getItem(ACCOUNTS_TABLE_SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        
        // Yeni eklenmiş sütun varsa, varsayılan ayarlarını koruyarak ekle
        const updatedColumns = tableColumns.map(defaultColumn => {
          const savedColumn = parsedSettings.find((col: TableColumn) => col.id === defaultColumn.id);
          if (savedColumn) {
            return {
              ...defaultColumn,
              visible: defaultColumn.fixed ? true : savedColumn.visible,
              sortable: savedColumn.sortable ?? defaultColumn.sortable ?? false,
            };
          }
          return defaultColumn;
        });
        
        setTableColumns(updatedColumns);
      } catch (e) {
        console.error('Kayıtlı tablo ayarları yüklenirken hata:', e);
      }
    }
  }, []);

  // Şirket ID'sini, hesapları ve grupları yükleme
  const dataLoadedRef = React.useRef(false);
  
  useEffect(() => {
    // Zaten yüklendiyse tekrar yükleme
    if (dataLoadedRef.current) {
      return;
    }
    
    const loadCompanyAndData = async () => {
      console.log('🔄 [ACCOUNTS] loadCompanyAndData başladı...');
      
      try {
        setLoading(true);
        
        // 🔍 OFFLINE DEBUG - AuthContext'ten companyId alınabilir mi?
        if (userDetails?.companyId) {
          console.log('✅ [ACCOUNTS] AuthContext\'ten companyId alındı:', userDetails.companyId);
          setCompanyId(userDetails.companyId);
          dataLoadedRef.current = true;
          
          // Cari grupları yükle
          console.log('📥 [ACCOUNTS] Cari grupları yükleniyor (SQL API)...');
          await loadAccountGroups(userDetails.companyId);
          
          // Cari hesapları yükle
          console.log('📥 [ACCOUNTS] Cari hesapları yükleniyor (SQL API)...');
          await loadAccounts(userDetails.companyId);
          
          return;
        }
        
        // Fallback: Firebase'den kullanıcı bilgilerini al
        console.log('⚠️ [ACCOUNTS] AuthContext\'te companyId yok, Firebase\'den alınıyor...');
        
        // Kullanıcı oturum açmış mı kontrol et
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          console.error('❌ [ACCOUNTS] Firebase auth.currentUser YOK');
          setLoading(false);
          setError('Kullanıcı oturumu açık değil.');
          return;
        }
        
        console.log('📡 [ACCOUNTS] Cloud Functions users/ okunuyor...');
        // Kullanıcının şirket ID'sini al (Cloud Functions)
        const userData = await firebaseDataService.getUserData(uid);
        
        if (!userData) {
          console.error('❌ [ACCOUNTS] Cloud Functions users/ verisi YOK');
          setLoading(false);
          setError('Kullanıcı bilgileri bulunamadı.');
          return;
        }
        
        const fetchedCompanyId = userData.companyId;
        console.log('✅ [ACCOUNTS] Cloud Functions\'dan companyId alındı:', fetchedCompanyId);
        setCompanyId(fetchedCompanyId || null);
        dataLoadedRef.current = true;
        
        if (!fetchedCompanyId) {
          console.error('❌ [ACCOUNTS] companyId boş');
          setLoading(false);
          setError('Şirket bilgisi bulunamadı.');
          return;
        }
        
        // Cari grupları yükle
        console.log('📥 [ACCOUNTS] Cari grupları yükleniyor (SQL API)...');
        await loadAccountGroups(fetchedCompanyId);
        
        // Cari hesapları yükle
        console.log('📥 [ACCOUNTS] Cari hesapları yükleniyor (SQL API)...');
        await loadAccounts(fetchedCompanyId);
        
      } catch (error) {
        console.error('❌ [ACCOUNTS] Veri yüklenirken hata:', error);
        setLoading(false);
        setError('Veriler yüklenirken bir hata oluştu.');
      }
    };
    
    // userDetails henüz yüklenmediyse bekle
    if (userDetails?.companyId || ownAuthSession.getCurrentUid()) {
      loadCompanyAndData();
    }
    
    // Cleanup
    return () => {};
  }, [userDetails?.companyId]);

  // Cari gruplarını yükleme - Sadece SQL'den
  const loadAccountGroups = async (companyId: string) => {
    await loadAccountGroupsFromSQL(companyId);
  };

  // 🔥 YENİ: SQL'den cari gruplarını yükleme
  const loadAccountGroupsFromSQL = async (companyId: string) => {
    const apiUrl = ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY;
    console.log('🌐 [ACCOUNTS-GROUPS] SQL API çağrısı:', apiUrl);
    
    try {
      const response = await accountingApi.get(apiUrl);

      console.log('📡 [ACCOUNTS-GROUPS] API yanıt status:', response.status);

      const result = response.data;
      const sqlGroups = result.data || result; // {success: true, data: [...]} formatını handle et
      
      console.log('✅ [ACCOUNTS-GROUPS] SQL\'den', sqlGroups.length, 'grup yüklendi');
      
      // SQL'den gelen verileri frontend formatına çevir
      const groupsList: AccountGroup[] = sqlGroups.map((data: any) => ({
        id: data.firebaseId || data.id.toString(), // Firebase ID varsa onu kullan, yoksa SQL ID
        sqlId: data.id != null ? data.id.toString() : null,
        name: decodeMojibake(data.name || ''),
        parentId: data.parentFirebaseId || null, // Frontend'de parent ID olarak Firebase ID kullanılıyor
        level: data.level || 1
      }));
      
      setGroups(groupsList);
      
      // Genel Cari Grubu ve Hesabı kontrolü
      const generalGroup = groupsList.find(group => decodeMojibake(group.name) === 'Genel Cari Grubu');
      let generalGroupId = generalGroup?.id || null;
      
      if (!generalGroup) {
        // Genel Cari Grubu oluştur
        console.log('⚠️ [ACCOUNTS-GROUPS] Genel Cari Grubu bulunamadı, oluşturuluyor...');
        generalGroupId = await createGeneralAccountGroup(companyId);
      }
      
      // Genel Cari hesabı kontrolü
      if (generalGroupId) {
        await checkAndCreateGeneralAccount(companyId, generalGroupId);
      }
      
    } catch (error: any) {
      console.error('❌ [ACCOUNTS-GROUPS] SQL yükleme hatası:', error.message || error);
      console.error('❌ [ACCOUNTS-GROUPS] Detay:', error);
      setError(handleApiError(error));
    }
  };

  // Genel Cari Grubu oluşturma - SQL API
  const createGeneralAccountGroup = async (companyId: string): Promise<string | null> => {
    console.log('📝 [ACCOUNTS] Genel Cari Grubu oluşturuluyor (SQL API)...');
    
    try {
      const generalGroupData = {
        companyId: companyId,
        name: "Genel Cari Grubu",
        parentId: null,
        level: 1,
        branchId: null,
        createdAt: new Date().toISOString(),
        createdBy: ownAuthSession.getCurrentUid() || null,
      };
      
      // SQL API'ye POST isteği
      const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_GROUPS, generalGroupData);

      const result = response.data;
      const newGroupId = result.data?.id?.toString() || null;
      
      console.log('✅ [ACCOUNTS] Genel Cari Grubu oluşturuldu, ID:', newGroupId);
      
      return newGroupId;
    } catch (error: any) {
      console.error('❌ [ACCOUNTS] Genel Cari Grubu hatası:', error.message || error);
      return null;
    }
  };

  // Genel Cari hesabı oluşturma ve kontrol - SQL API
  const checkAndCreateGeneralAccount = async (companyId: string, groupId: string | null) => {
    try {
      if (!groupId) {
        console.warn('⚠️ [ACCOUNTS] Genel Cari oluşturulamıyor: Grup ID yok');
        return;
      }
      
      // Genel Cari var mı kontrol et (bu zaten loadAccountsFromSQL ile birlikte çağrılıyor)
      // Bu nedenle ayrı bir API çağrısı yapmak yerine mevcut accounts state'ini kontrol edebiliriz
      // Ancak bu fonksiyon loadAccounts'tan ÖNCE çağrılıyor, bu yüzden API çağrısı yapılıyor
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);

      const result = response.data;
      const sqlAccounts = result.data || result;
      
      // "Genel Cari" var mı kontrol et
      const generalAccount = sqlAccounts.find((acc: any) => acc.companyName === "Genel Cari");
      
      if (generalAccount) {
        // Genel Cari zaten var, log yazmaya gerek yok
        return;
      }
      
      // Genel Cari hesabını oluştur
      console.log('📝 [ACCOUNTS] Genel Cari bulunamadı, oluşturuluyor...');
      
      const generalAccountData = {
        companyId: companyId,
        companyName: "Genel Cari",
        groupFirebaseId: groupId, // SQL'deki grup ID'sini kullan
        authorizedPerson: "",
        phone: "",
        mobilePhone: "",
        email: "",
        website: "",
        postalCode: "",
        taxOffice: "",
        taxNumber: "11111111111",
        tradeRegisterNumber: "",
        country: "Türkiye",
        city: "",
        district: "",
        address: "",
        accountCode: "GC001",
        notes: "Varsayılan cari hesap",
        riskLimitAmount: null,
        preventNegativeBalance: false,
        isActive: true,
        branchId: null,
        createdAt: new Date().toISOString(),
        createdBy: ownAuthSession.getCurrentUid() || null,
      };
      
      // SQL API'ye POST isteği
      const createResponse = await accountingApi.post(ACCOUNTING_API.ACCOUNTS, generalAccountData);

      const createResult = createResponse.data;
      console.log('✅ [ACCOUNTS] Genel Cari oluşturuldu, ID:', createResult.data?.id);
      
    } catch (error: any) {
      console.error('❌ [ACCOUNTS] Genel Cari hatası:', error.message || error);
    }
  };

  /**
   * Grup arama tablosu: aynı grup hem Firebase ID hem SQL ID ile aranabilir.
   * Cari kaydındaki grup referansı (groupFirebaseId / groupId) veriye göre bu iki
   * biçimden herhangi birini taşıyabildiği için tek anahtarla arama yapılınca
   * grup bulunamıyor ve tabloda "-" görünüyordu.
   */
  const groupLookup = useMemo(() => {
    const map = new Map<string, AccountGroup>();
    groups.forEach(g => {
      map.set(g.id, g);
      if (g.sqlId) map.set(g.sqlId, g);
    });
    return map;
  }, [groups]);

  // ✅ Server-side sayfalı cari hesap yükleme
  const loadAccountsPaged = useCallback(async (
    cid: string,
    page: number = serverPage,
    pageSize: number = serverRowsPerPage,
    search: string = debouncedSearchTerm,
    groupId: string | null = selectedGroupFilter?.sqlId ?? selectedGroupFilter?.id ?? null,
    sc: string = sortColumn,
    sd: string = sortDirection
  ) => {
    const fetchId = ++fetchIdRef.current; // Race condition koruması
    // Sunucu sayısal grup ID bekler; Firebase ID gelirse SQL karşılığına çevir
    const resolvedGroupId = groupId
      ? (groupLookup.get(groupId)?.sqlId ?? groupId)
      : null;
    const parsedGroupId = resolvedGroupId ? parseInt(resolvedGroupId, 10) : NaN;
    const groupIdNum = isNaN(parsedGroupId) ? undefined : parsedGroupId;
    const apiUrl = ACCOUNTING_API.ACCOUNT_BY_COMPANY_PAGED(
      page + 1, pageSize, search || undefined, groupIdNum, sc, sd, true, effectiveBranchId
    );
    console.log('🌐 [ACCOUNTS-LIST] Sayfalı API çağrısı:', apiUrl);

    try {
      setLoading(true);

      const response = await accountingApi.get(apiUrl);

      // Race condition: eski istek sonucu geldi, atla
      if (fetchId !== fetchIdRef.current) return;

      const result = response.data;
      const data = result.data || result;
      const pagination = result.pagination || {};
      const aggregates = result.aggregates || {};

      console.log('📊 [ACCOUNTS-LIST] API RAW response:', JSON.stringify({ pagination, aggregates }, null, 2));
      console.log('📊 [ACCOUNTS-LIST] API RAW data (ilk 5):', (Array.isArray(data) ? data : []).slice(0, 5).map((d: any) => ({
        id: d.id, companyName: d.companyName, creditBalance: d.creditBalance, debitBalance: d.debitBalance, balance: d.balance, isActive: d.isActive
      })));

      const accountsList: Account[] = (Array.isArray(data) ? data : []).map((d: any) => ({
        id: d.id.toString(),
        companyName: decodeMojibake(d.companyName || ''),
        groupId: d.groupFirebaseId || d.groupId?.toString() || null,
        authorizedPerson: decodeMojibake(d.authorizedPerson || ''),
        mobilePhone: decodeMojibake(d.mobilePhone || ''),
        taxNumber: decodeMojibake(d.taxNumber || ''),
        accountCode: decodeMojibake(d.accountCode || ''),
        createdAt: d.createdAt || '',
        balance: d.creditBalance || 0,
        debitBalance: d.debitBalance || 0,
        branchId: d.branchId || null,
        phone: decodeMojibake(d.phone || ''),
        email: decodeMojibake(d.email || ''),
        website: decodeMojibake(d.website || ''),
        postalCode: decodeMojibake(d.postalCode || ''),
        taxOffice: decodeMojibake(d.taxOffice || ''),
        tradeRegisterNumber: decodeMojibake(d.tradeRegisterNumber || ''),
        country: decodeMojibake(d.country || ''),
        city: decodeMojibake(d.city || ''),
        district: decodeMojibake(d.district || ''),
        address: decodeMojibake(d.address || ''),
        notes: decodeMojibake(d.notes || ''),
        isActive: d.isActive !== false,
        preventNegativeBalance: d.preventNegativeBalance === true,
        // B2B / e-ticaret alanları: düzenleme modalı bu satırdan prefill yaptığı için
        // buraya eklenmezse form varsayılanları (30 gün, boş iskonto) gösterir ve
        // kaydetme sırasında gerçek değerlerin üzerine yazar.
        riskLimitAmount: d.riskLimitAmount ?? null,
        riskLimitCurrency: d.riskLimitCurrency || null,
        paymentDueDays: d.paymentDueDays ?? null,
        riskLimitAction: d.riskLimitAction || null,
        isEcommerceUser: d.isEcommerceUser === true,
        isB2BDealer: d.isB2BDealer === true,
        ecommerceDiscountPercent: d.ecommerceDiscountPercent ?? null,
        ecommercePriceBasis: d.ecommercePriceBasis || null,
        blockOrdersWhenOverdue: d.blockOrdersWhenOverdue === true
      }));

      console.log('📊 [ACCOUNTS-LIST] Hesaplanan bakiyeler:', accountsList.map(a => ({
        id: a.id, companyName: a.companyName, creditBalance: a.balance, debitBalance: a.debitBalance, netBakiye: (a.balance || 0) - (a.debitBalance || 0)
      })));

      setAccounts(accountsList as AccountDetails[]);
      setFilteredAccounts(accountsList);
      setTotalCount(pagination.totalCount || 0);
      setServerTotalCredit(aggregates.totalCredit || 0);
      setServerTotalDebit(aggregates.totalDebit || 0);
      setLoading(false);

      console.log(`✅ [ACCOUNTS-LIST] ${accountsList.length} hesap yüklendi (Toplam: ${pagination.totalCount || 0}), Aggregates: totalCredit=${aggregates.totalCredit}, totalDebit=${aggregates.totalDebit}`);
    } catch (error: any) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('❌ [ACCOUNTS-LIST] Yükleme hatası:', error.message || error);
      setLoading(false);
      setError(handleApiError(error));
    }
  }, [serverPage, serverRowsPerPage, debouncedSearchTerm, selectedGroupFilter, sortColumn, sortDirection, effectiveBranchId, groupLookup]);

  // loadAccounts — tüm çağrı noktaları için uyumlu wrapper
  const loadAccounts = useCallback(async (cid: string) => {
    await loadAccountsPaged(cid);
  }, [loadAccountsPaged]);

  // İstatistik hesaplamaları — Server-side aggregate kullanır (tüm filtrelenmiş kayıtlar)
  const accountStats = useMemo(() => {
    const totalBalance = serverTotalCredit - serverTotalDebit;
    return { totalBalance, totalCredit: serverTotalCredit, totalDebit: serverTotalDebit, accountCount: totalCount };
  }, [serverTotalCredit, serverTotalDebit, totalCount]);

  const calculateBalance = (account: Account): number => {
    const debitBalance = account.debitBalance || 0;
    const creditBalance = account.balance || 0;
    return creditBalance - debitBalance;
  };

  // Grup adını ID'ye göre bul
  const getGroupName = (groupId: string | null): string => {
    if (!groupId) return '-';

    const group = groupLookup.get(groupId);
    if (!group) return '-';

    const decodedGroupName = decodeMojibake(group.name);
    const normalized = decodedGroupName.trim().toLocaleLowerCase('tr-TR');
    const keyMap: Record<string, string> = {
      'bireysel cari': 'individualCurrent',
      'personel cari': 'personnelCurrent',
      'personal cari': 'personnelCurrent',
      'kurumsal cari': 'corporateCurrent',
      'genel cari grubu': 'generalCurrentGroup'
    };
    const key = keyMap[normalized];
    return key ? t(`current.groupNames.${key}`, { defaultValue: decodedGroupName }) : decodedGroupName;
  };

  // Şube adını ID'ye göre bul
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return '-';
    
    const branch = branches.find(b => b.id === branchId);
    if (!branch) return '-';
    
    return branch.name;
  };

  const handleOpenGroupModal = () => {
    setIsGroupModalOpen(true);
  };

  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
    // Grup modalı kapandığında, en güncel grup verilerini tekrar yükle
    if (companyId) {
      loadAccountGroups(companyId);
    }
  };

  const handleOpenAddAccountModal = () => {
    setEditingAccount(null); // Yeni ekleme modu
    setIsAddAccountModalOpen(true);
  };

  const handleCloseAddAccountModal = () => {
    setIsAddAccountModalOpen(false);
    setEditingAccount(null); // Düzenleme state'ini temizle
    // Cari ekleme modalı kapandığında, en güncel hesap verilerini tekrar yükle
    if (companyId) {
      loadAccounts(companyId);
    }
  };
  
  // ✅ YENİ: Düzenleme modalını aç
  const handleOpenEditAccountModal = (account: Account) => {
    console.log('🔄 Cari düzenleme modu açılıyor:', account);
    setEditingAccount(account);
    setIsAddAccountModalOpen(true);
  };

  // Hesap düzenleme işlemi (placeholder)
  // Hesap düzenleme işlemi - SQL API
  const handleEditAccount = async (account: Account) => {
    console.log('Düzenleme:', account);
    // TODO: Düzenleme modalı açılacak
    // Şu an için sadece bilgilendirme
    alert('Cari düzenleme özelliği yakında eklenecek. Düzenlemek için AddAccount modalı açılabilir.');
  };

  // Hesap silme işlemi (soft delete) - SQL API
  const handleDeleteAccount = async (account: Account) => {
    if (!window.confirm(`"${account.companyName}" hesabını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }

    try {
      // Account ID'yi sayıya çevir (SQL ID)
      const numericId = parseInt(account.id, 10);
      if (isNaN(numericId)) {
        throw new Error('Geçersiz hesap ID formatı');
      }

      console.log('🔄 Hesap siliniyor (SQL ID:', numericId, ')');

      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error('CompanyId bulunamadı');
      }

      // SQL API'ye istek gönder
      await accountingApi.post(ACCOUNTING_API.ACCOUNT_DELETE(numericId));

      console.log(`✅ ${account.companyName} hesabı silindi (SQL)`);
      alert('Cari hesap başarıyla silindi!');
      
      // Listeyi yenile
      if (companyId) {
        await loadAccountsPaged(companyId);
      }
    } catch (error: any) {
      console.error('Cari hesap silinirken hata:', error);
      alert(handleApiError(error));
    }
  };

  // ✅ Server-side arama/filtreleme/sıralama — debouncedSearchTerm, grup, sıralama değiştiğinde refetch
  useEffect(() => {
    if (companyId) {
      loadAccountsPaged(companyId, serverPage, serverRowsPerPage, debouncedSearchTerm, 
        selectedGroupFilter?.sqlId ?? selectedGroupFilter?.id ?? null, sortColumn, sortDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedGroupFilter, sortColumn, sortDirection, serverPage, serverRowsPerPage, effectiveBranchId]);
  
  // Mobil sayfalandırılmış hesaplar — server-side dönen veri zaten sayfalı
  const paginatedAccounts = useMemo(() => {
    return filteredAccounts; // Artık server zaten sayfalı döndürüyor
  }, [filteredAccounts]);
  
  // Filtrelenmiş hesapların genel toplam bakiyesini hesapla — server aggregate kullan
  const totalBalance = useMemo(() => {
    return serverTotalCredit - serverTotalDebit;
  }, [serverTotalCredit, serverTotalDebit]);
  
  // Mobil sayfa değişimi
  // Mobil sayfa değişimi — server-side sayfalama ile senkron
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setMobilePage(newPage);
    setServerPage(newPage);
  };

  // Mobil sayfa başına satır sayısı değişimi — server-side sayfalama ile senkron
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newSize = parseInt(event.target.value, 10);
    setMobileRowsPerPage(newSize);
    setServerRowsPerPage(newSize);
    setMobilePage(0);
    setServerPage(0);
  };

  // Cari detay modalını açma
  const handleOpenDetailModal = (account: AccountDetails) => {
    setSelectedAccount(account);
    setIsDetailModalOpen(true);
  };

  // Cari detay modalını kapatma
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedAccount(null);
  };

  // Pasif hesaplar modalını açma
  const handleOpenPassiveAccountsModal = () => {
    setIsPassiveAccountsModalOpen(true);
  };

  // Pasif hesaplar modalını kapatma
  const handleClosePassiveAccountsModal = () => {
    setIsPassiveAccountsModalOpen(false);
  };

  // Pasif hesap aktifleştirildiğinde
  const handleAccountActivated = () => {
    if (companyId) {
      loadAccounts(companyId);
    }
  };

  // Cari hareket detayları modalını açma
  const handleOpenTransactionsModal = (account: AccountDetails, e: React.MouseEvent) => {
    e.stopPropagation(); // Ana satır tıklamasını durdur
    if (isManager) {
      // Manager panelinde admin rotasına gidilemez; rapor modal içinde açılır
      setManagerReportAccountId(account.id);
      return;
    }
    navigate(`/cari/hareket/${account.id}`);
  };

  // Cari hareket detayları modalını kapatma - artık gerekli değil, çünkü yeni sayfaya yönlendiriyoruz
  const handleCloseTransactionsModal = () => {
    setIsTransactionsModalOpen(false);
  };

  // Sevk adresi modalını açma
  const handleOpenShippingAddressModal = (account: AccountDetails, e: React.MouseEvent) => {
    e.stopPropagation(); // Ana satır tıklamasını durdur
    
    // Genel Cari için koruma
    if (isGeneralAccountName(account.companyName)) {
      setError("Genel Cari hesabı için sevk adresi eklenemez. Lütfen başka bir cari hesap seçin.");
      return;
    }
    
    setSelectedAccount(account);
    setIsShippingAddressModalOpen(true);
  };

  // Sevk adresi modalını kapatma
  const handleCloseShippingAddressModal = () => {
    setIsShippingAddressModalOpen(false);
  };

  // Tahsilat yapma modalını aç
  const handleOpenCollectPaymentModal = (accId: string, accName: string) => {
    // Genel Cari için koruma
    if (accName === "Genel Cari") {
      setError("Genel Cari hesabı için tahsilat işlemi yapılamaz. Lütfen başka bir cari hesap seçin.");
      return;
    }
    
    setSelectedAccount({ id: accId, companyName: accName } as AccountDetails);
    setIsCollectPaymentModalOpen(true);
  };

  // Tahsilat yapma modalını kapat
  const handleCloseCollectPaymentModal = () => {
    setIsCollectPaymentModalOpen(false);
  };

  // Ödeme yapma modalını aç
  const handleOpenMakePaymentModal = (accId: string, accName: string) => {
    // Genel Cari için koruma
    if (accName === "Genel Cari") {
      setError("Genel Cari hesabı için ödeme işlemi yapılamaz. Lütfen başka bir cari hesap seçin.");
      return;
    }
    
    setSelectedAccount({ id: accId, companyName: accName } as AccountDetails);
    setIsMakePaymentModalOpen(true);
  };

  // Ödeme yapma modalını kapat
  const handleCloseMakePaymentModal = () => {
    setIsMakePaymentModalOpen(false);
  };

  // Cariyi borçlandır (tahakkuk) modalını aç
  const handleOpenDebitModal = (accId: string, accName: string) => {
    // Genel Cari için koruma
    if (isGeneralAccountName(accName)) {
      setError("Genel Cari hesabı borçlandırılamaz. Lütfen başka bir cari hesap seçin.");
      return;
    }

    setSelectedAccount({ id: accId, companyName: accName } as AccountDetails);
    setIsDebitModalOpen(true);
  };

  // Cariyi borçlandır modalını kapat
  const handleCloseDebitModal = () => {
    setIsDebitModalOpen(false);
  };

  // Tablo ayarlarını kaydet
  const saveTableSettings = (columns: TableColumn[]) => {
    setTableColumns(columns);
    localStorage.setItem(ACCOUNTS_TABLE_SETTINGS_KEY, JSON.stringify(columns));
  };

  const handleOpenTableSettingsModal = () => {
    setIsTableSettingsModalOpen(true);
  };

  const handleCloseTableSettingsModal = () => {
    setIsTableSettingsModalOpen(false);
  };

  // Tablo sütun ayarlarını güncelle
  const handleUpdateTableSettings = (columns: TableColumn[]) => {
    saveTableSettings(columns);
  };

  // Para birimi formatla
  const formatCurrency = (amount: number) => {
    // Mutlak değeri al, renkle göstereceğiz zaten negatif/pozitif durumunu
    const absoluteAmount = Math.abs(amount);
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absoluteAmount);
    return `${formatted} ₺`; // Para birimi sembolünü sağa al
  };

  const getColumnLabel = (columnId: string) => {
    const keyMap: Record<string, string> = {
      rowNumber: 'current.table.columns.rowNumber',
      companyName: 'current.table.columns.companyName',
      group: 'current.table.columns.group',
      authorizedPerson: 'current.table.columns.authorizedPerson',
      mobilePhone: 'current.table.columns.mobilePhone',
      taxNumber: 'current.table.columns.taxNumber',
      branch: 'current.table.columns.branch',
      balance: 'current.table.columns.balance',
      actions: 'current.table.columns.actions',
    };
    const key = keyMap[columnId];
    return key ? t(key) : columnId;
  };

  // AccountingDataTable için dinamik sütun tanımları
  const accountColumns = useMemo<DataTableColumn<Account>[]>(() => {
    return tableColumns
      .filter(col => col.visible)
      .map(col => {
        const base: DataTableColumn<Account> = {
          id: col.id,
          label: getColumnLabel(col.id),
          field: col.field as keyof Account,
          sortable: col.sortable ?? false,
        };
        switch (col.id) {
          case 'rowNumber':
            return { ...base, width: '60px', render: (_row: Account, index?: number) => index !== undefined ? (serverPage * serverRowsPerPage) + index + 1 : '-' };
          case 'companyName':
            return { ...base, render: (row: Account) => {
              const name = row.companyName && row.companyName !== '-' ? decodeMojibake(row.companyName) : '';
              const person = row.authorizedPerson && row.authorizedPerson !== '-' ? decodeMojibake(row.authorizedPerson) : '';
              const displayName = name || person || '-';
              return (
                <Tooltip title={displayName} placement="top-start">
                  <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{displayName}</span>
                </Tooltip>
              );
            }};
          case 'group':
            return { ...base, render: (row: Account) => getGroupName(row.groupId) };
          case 'authorizedPerson':
            return { ...base, render: (row: Account) => decodeMojibake(row.authorizedPerson) || '-' };
          case 'mobilePhone':
            return { ...base, render: (row: Account) => row.mobilePhone || '-' };
          case 'taxNumber':
            return { ...base, render: (row: Account) => row.taxNumber || '-' };
          case 'branch':
            return { ...base, render: (row: Account) => getBranchName(row.branchId) };
          case 'balance':
            return { ...base, headerAlign: 'right' as const, align: 'right' as const, render: (row: Account) => {
              const bal = calculateBalance(row);
              return (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: bal >= 0 ? 'success.main' : 'error.main', fontSize: '0.8rem' }}
                >
                  {formatCurrency(bal)}
                </Typography>
              );
            }};
          case 'actions':
            return { ...base, width: '100px', headerAlign: 'right' as const, align: 'right' as const, render: (row: Account) => (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsMenuAnchorEl(e.currentTarget);
                  setSelectedAccountForMenu(row);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )};
          default:
            return base;
        }
      });
    // Gruplar ve şubeler listeden sonra (asenkron) yüklendiği için bağımlılıkta yer alır:
    // aksi halde render fonksiyonları ilk render'daki boş listeleri kapsayıp
    // grup/şube sütununda kalıcı olarak "-" gösteriyordu. Sayfa bilgisi de sıra
    // numarasının doğru hesaplanması için gereklidir.
  }, [tableColumns, t, groupLookup, branches, serverPage, serverRowsPerPage]);

  const localizedTableColumns = useMemo<TableColumn[]>(
    () => tableColumns.map((col) => ({ ...col, label: getColumnLabel(col.id) })),
    [tableColumns, t]
  );

  // Cash transfer modalını açma
  const handleOpenCashTransferModal = () => {
    setIsCashTransferModalOpen(true);
  };

  // Cash transfer modalını kapatma
  const handleCloseCashTransferModal = () => {
    setIsCashTransferModalOpen(false);
  };

  // Yeni modal için açma fonksiyonu (şimdilik console.log)
  const handleOpenBulkAddAccountModal = () => {
    setIsBulkAddAccountModalOpen(true);
  };

  // Toplu cari ekleme modalını kapatma fonksiyonu
  const handleCloseBulkAddAccountModal = () => {
    setIsBulkAddAccountModalOpen(false);
  };

  // Toplu cariler eklendikten sonra çağrılacak fonksiyon
  const handleAccountsAdded = () => {
    if (companyId) {
      loadAccounts(companyId);
    }
  };

  // Cari hesap yazdırma fonksiyonu
  const handlePrintAccount = (account: Account) => {
    const balance = calculateBalance(account);
    const groupName = getGroupName(account.groupId);
    const branchName = getBranchName(account.branchId);
    const balanceColor = balance >= 0 ? '#4caf50' : '#f44336';
    const balanceText = balance >= 0 ? 'ALACAK' : 'BORÇ';
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup engelleyici nedeniyle yazdırma penceresi açılamadı. Lütfen popup engelleyiciyi devre dışı bırakın.');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Cari Hesap Bilgileri</title>
        <style>
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            /* Tarayıcının otomatik eklediği başlık/altbilgileri gizle */
            @page {
              margin-top: 0;
              margin-bottom: 0;
            }
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            background: #f5f5f5;
          }
          
          .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #1976d2;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header h1 {
            color: #1976d2;
            font-size: 28px;
            margin-bottom: 5px;
          }
          
          .header p {
            color: #666;
            font-size: 14px;
          }
          
          .info-section {
            margin-bottom: 30px;
          }
          
          .info-section h2 {
            color: #1976d2;
            font-size: 18px;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          
          .info-item {
            padding: 10px;
            background: #f9f9f9;
            border-radius: 4px;
          }
          
          .info-item label {
            display: block;
            color: #666;
            font-size: 12px;
            margin-bottom: 5px;
            font-weight: 600;
          }
          
          .info-item value {
            display: block;
            color: #333;
            font-size: 14px;
            font-weight: 500;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
          
          .print-date {
            color: #999;
            font-size: 11px;
            margin-top: 10px;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .container {
              box-shadow: none;
              padding: 20px;
            }
            
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <h1>CARİ HESAP BİLGİLERİ</h1>
            <p>Detaylı Cari Raporu</p>
          </div>

          <!-- Temel Bilgiler -->
          <div class="info-section">
            <h2>📋 Temel Bilgiler</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>Cari Adı</label>
                <value>${decodeMojibake(account.companyName)}</value>
              </div>
              <div class="info-item">
                <label>Cari Kodu</label>
                <value>${account.accountCode || '-'}</value>
              </div>
              <div class="info-item">
                <label>Cari Grup</label>
                <value>${groupName}</value>
              </div>
              <div class="info-item">
                <label>Yetkili Kişi</label>
                <value>${decodeMojibake(account.authorizedPerson) || '-'}</value>
              </div>
            </div>
          </div>

          <!-- İletişim Bilgileri -->
          <div class="info-section">
            <h2>📞 İletişim Bilgileri</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>Cep Telefonu</label>
                <value>${account.mobilePhone || '-'}</value>
              </div>
              <div class="info-item">
                <label>Şube</label>
                <value>${branchName}</value>
              </div>
            </div>
          </div>

          <!-- Vergi Bilgileri -->
          <div class="info-section">
            <h2>🧾 Vergi Bilgileri</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>Vergi Numarası</label>
                <value>${account.taxNumber || '-'}</value>
              </div>
              <div class="info-item">
                <label>Oluşturma Tarihi</label>
                <value>${new Date(account.createdAt).toLocaleDateString('tr-TR')}</value>
              </div>
            </div>
          </div>

          <!-- Bakiye Detayları -->
          <div class="info-section">
            <h2>💰 Bakiye Detayları</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>Alacak Bakiyesi</label>
                <value style="color: #4caf50; font-weight: bold;">${formatCurrency(account.balance || 0)}</value>
              </div>
              <div class="info-item">
                <label>Borç Bakiyesi</label>
                <value style="color: #f44336; font-weight: bold;">${formatCurrency(account.debitBalance || 0)}</value>
              </div>
              <div class="info-item">
                <label>Net Bakiye</label>
                <value style="color: ${balanceColor}; font-weight: bold; font-size: 16px;">${formatCurrency(balance)} (${balanceText})</value>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p><strong>AQUASOFT</strong></p>
            <p class="print-date">Yazdırma Tarihi: ${new Date().toLocaleString('tr-TR')}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
            // Yazdırma işlemi tamamlandıktan sonra pencereyi kapat
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('current.export.excel.sheetName'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: t('current.export.excel.columns.companyName'), key: 'companyName', width: 30 },
        { header: t('current.export.excel.columns.groupName'), key: 'groupName', width: 20 },
        { header: t('current.export.excel.columns.authorizedPerson'), key: 'authorizedPerson', width: 20 },
        { header: t('current.export.excel.columns.mobilePhone'), key: 'mobilePhone', width: 15 },
        { header: t('current.export.excel.columns.taxNumber'), key: 'taxNumber', width: 15 },
        { header: t('current.export.excel.columns.branchName'), key: 'branchName', width: 20 },
        { header: t('current.export.excel.columns.balance'), key: 'balance', width: 15 },
        { header: t('current.export.excel.columns.status'), key: 'isActive', width: 10 },
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Cari hesapları Excel'e aktar
      accounts.forEach(account => {
        const balance = calculateBalance(account);
        
        worksheet.addRow({
          companyName: decodeMojibake(account.companyName),
          groupName: getGroupName(account.groupId),
          authorizedPerson: decodeMojibake(account.authorizedPerson) || '-',
          mobilePhone: account.mobilePhone || '-',
          taxNumber: account.taxNumber || '-',
          branchName: getBranchName(account.branchId),
          balance: `${formatCurrency(balance).replace('₺', '').trim()} TL`,
          isActive: account.isActive ? t('current.export.excel.status.active') : t('current.export.excel.status.passive')
        });
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${t('current.export.excel.filePrefix')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${accounts.length} adet cari hesap Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError('Excel dışa aktarma sırasında bir hata oluştu.');
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık ve Butonlar */}
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 }, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <PeopleIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('current.title')}
          </Typography>
        </Box>
      </HeaderContainer>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && accounts.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AccountBalanceWalletIcon />}
            color={accountStats.totalBalance >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
            title={formatStatsCurrency(accountStats.totalBalance)}
            subtitle={t('current.stats.totalNetBalance')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={STATS_COLORS.blue}
            title={formatStatsCurrency(accountStats.totalCredit)}
            subtitle={t('current.stats.totalCredit')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingDownIcon />}
            color={STATS_COLORS.orange}
            title={formatStatsCurrency(accountStats.totalDebit)}
            subtitle={t('current.stats.totalDebit')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<PeopleIcon />}
            color={STATS_COLORS.purple}
            title={`${accountStats.accountCount}`}
            subtitle={t('current.stats.totalCurrentAccounts')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('current.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Grup Filtresi */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={[allGroupsOption, ...groups]}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedGroupFilter || allGroupsOption}
              onChange={(_, newValue) => {
                if (newValue && newValue.id === 'all') {
                  setSelectedGroupFilter(null);
                } else {
                  setSelectedGroupFilter(newValue as AccountGroup | null);
                }
                setServerPage(0);
                setMobilePage(0);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('current.filters.groupFilter')}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 250,
                },
              }}
            />
          </Grid>

          {/* Şube Filtresi — manager için kilitli (kendi şubesi seçili) */}
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label={isManager ? t('current.filters.lockedBranch') : t('current.filters.branchFilter')}
              value={effectiveBranchId || 'all'}
              disabled={isManager}
              onChange={(e) => {
                setSelectedBranchFilter(e.target.value === 'all' ? null : e.target.value);
                setServerPage(0);
                setMobilePage(0);
              }}
            >
              <MenuItem value="all">{t('current.filters.allBranches')}</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddAccountModal}
              >
                {t('current.actions.newCurrentAccount')}
              </Button>
              {!isManager && (
              <Button 
                variant="outlined" 
                color="secondary"
                startIcon={<PeopleIcon />}
                onClick={handleOpenBulkAddAccountModal}
              >
                {t('current.actions.bulkAddCurrentAccount')}
              </Button>
              )}
              <Button 
                variant="outlined"
                onClick={handleOpenMoreMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddAccountModal}
                >
                  {t('current.actions.newCurrentShort')}
                </Button>
              </Grid>
              {!isManager && (
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="outlined" 
                  color="secondary"
                  startIcon={<PeopleIcon />}
                  onClick={handleOpenBulkAddAccountModal}
                >
                  {t('current.actions.bulkAddShort')}
                </Button>
              </Grid>
              )}
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenMoreMenu}
                  size="small"
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : totalCount === 0 && filteredAccounts.length === 0 ? (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <PeopleIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {t('current.emptyState.title')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? t('current.emptyState.searchNotFound') : t('current.emptyState.noRecords')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Grid container spacing={2} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
            {paginatedAccounts.map((account, index) => {
              const balance = calculateBalance(account);
              const isGeneralAccount = isGeneralAccountName(account.companyName);
              const rowNumber = (mobilePage * mobileRowsPerPage) + index + 1;
              
              return (
                <Card 
                  key={account.id}
                  elevation={2} 
                  sx={{ 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: balance >= 0 ? 'success.light' : 'error.light',
                    bgcolor: balance >= 0 ? 'success.50' : 'error.50',
                    '&:hover': { 
                      boxShadow: 4,
                      borderColor: balance >= 0 ? 'success.main' : 'error.main'
                    }
                  }}
                  onClick={() => handleOpenDetailModal(account as AccountDetails)}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    {/* Sıra Numarası ve Hesap Adı */}
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <Chip 
                        label={`#${rowNumber}`}
                        size="small"
                        sx={{ 
                          minWidth: '40px',
                          fontWeight: 'bold',
                          bgcolor: 'primary.main',
                          color: 'white'
                        }}
                      />
                      <PeopleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {(account.companyName && account.companyName !== '-' ? decodeMojibake(account.companyName) : '') || (account.authorizedPerson && account.authorizedPerson !== '-' ? decodeMojibake(account.authorizedPerson) : '') || '-'}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Cari Grup */}
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">{t('current.mobileCard.group')}:</Typography>
                      <Typography variant="caption" fontWeight="medium">{getGroupName(account.groupId)}</Typography>
                    </Box>

                    {/* Yetkili Kişi */}
                    {account.authorizedPerson && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">{t('current.mobileCard.authorized')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">{decodeMojibake(account.authorizedPerson)}</Typography>
                      </Box>
                    )}

                    {/* Vergi No */}
                    {account.taxNumber && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">{t('current.mobileCard.taxNumber')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">{account.taxNumber}</Typography>
                      </Box>
                    )}

                    {/* Şube */}
                    {account.branchId && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">{t('current.mobileCard.branch')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">{getBranchName(account.branchId)}</Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* Bakiye */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography variant="caption" color="text.secondary">{t('current.mobileCard.balance')}:</Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color={balance >= 0 ? 'success.main' : 'error.main'}
                        sx={{ fontSize: '1.1rem' }}
                      >
                        {formatCurrency(balance)}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* İşlem Butonları */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<AssessmentIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTransactionsModal(account as AccountDetails, e);
                          }}
                        >
                          {t('current.actions.currentReport')}
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<ArrowDownwardIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCollectPaymentModal(
                              account.id,
                              decodeMojibake(account.companyName) || ''
                            );
                          }}
                          disabled={isGeneralAccount}
                        >
                          {t('current.transactionDialog.collectPayment')}
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<ArrowUpwardIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMakePaymentModal(
                              account.id,
                              decodeMojibake(account.companyName) || ''
                            );
                          }}
                          disabled={isGeneralAccount}
                        >
                          {t('current.transactionDialog.makePayment')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<LocalShippingIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenShippingAddressModal(account as AccountDetails, e);
                          }}
                          disabled={isGeneralAccount}
                        >
                          {t('current.actions.addShippingAddress')}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Grid>

          {/* 🖥️ Desktop Table View (md+) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<Account>
              columns={accountColumns}
              rows={filteredAccounts}
              getRowId={(row) => row.id}
              loading={false}
              defaultRowsPerPage={50}
              rowsPerPageOptions={[25, 50, 100]}
              onRowClick={(row) => handleOpenDetailModal(row as AccountDetails)}
              clickable
              emptyMessage={t('current.table.noData')}
              emptyIcon={<PeopleIcon sx={{ fontSize: 48 }} />}
              ariaLabel={t('current.table.ariaLabel')}
              // ✅ Server-side pagination
              serverSidePagination={true}
              totalCount={totalCount}
              page={serverPage}
              pageSize={serverRowsPerPage}
              onPageChange={(newPage, newPageSize) => {
                setServerPage(newPage);
                setServerRowsPerPage(newPageSize);
              }}
              // ✅ Server-side sorting
              sortField={sortColumn}
              sortDirection={sortDirection}
              onSort={(field, direction) => {
                setSortColumn(field);
                setSortDirection(direction);
                setServerPage(0);
              }}
              columnResizeStorageKey="accounts"
            />
            {/* Genel Toplam */}
            {totalCount > 0 && (
              <Paper elevation={1} sx={{ mt: 1, p: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" fontWeight="bold">{t('current.table.grandTotal')}:</Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={totalBalance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(totalBalance)}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* 📱 Mobile Pagination */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={mobilePage}
              onPageChange={handleChangePage}
              rowsPerPage={mobileRowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('current.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
                t('current.pagination.displayedRows', { from, to, count })
              }
              sx={DATATABLE_STYLES.pagination}
            />
          </Box>
        </>
      )}

      {/* Üst Menü - 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          handleCloseMoreMenu();
          handleOpenTableSettingsModal();
        }}>
          <ListItemIcon>
            <ViewColumnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.customizeTable')} />
        </MenuItem>
        <MenuItem onClick={() => {
          handleCloseMoreMenu();
          handleOpenPassiveAccountsModal();
        }}>
          <ListItemIcon>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.passiveAccounts')} />
        </MenuItem>
        <MenuItem onClick={() => {
          handleCloseMoreMenu();
          if (isManager) {
            // Manager panelinde admin rotasına gidilemez; rapor modal içinde açılır
            setIsManagerMovementsReportOpen(true);
          } else {
            navigate('/cari-hareket-raporu');
          }
        }}>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.movementReport')} />
        </MenuItem>
        <MenuItem onClick={() => {
          handleCloseMoreMenu();
          handleOpenGroupModal();
        }}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.addGroup')} />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleCloseMoreMenu();
            setIsInfoModalOpen(true);
          }}
        >
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.information')} />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseMoreMenu();
          }}
          disabled={loadingExport || loading || accounts.length === 0}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={loadingExport ? t('current.menu.preparing') : t('current.menu.downloadExcel')} />
        </MenuItem>
      </Menu>

      {/* İşlemler Menüsü (3 Nokta) */}
      <Menu
        anchorEl={actionsMenuAnchorEl}
        open={Boolean(actionsMenuAnchorEl)}
        onClose={() => {
          setActionsMenuAnchorEl(null);
          setSelectedAccountForMenu(null);
        }}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: '200px' }
        }}
      >
        <MenuItem 
          onClick={(e) => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenTransactionsModal(selectedAccountForMenu as AccountDetails, e as any);
            }
          }}
        >
          <ListItemIcon>
            <AssessmentIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('current.actions.currentReport')} />
        </MenuItem>
        {/* "İşlem Yap" modalı kaldırıldı; Tahsilat ve Ödeme butonları doğrudan menüye taşındı (gereksiz ara modal). */}
        <MenuItem
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenCollectPaymentModal(
                selectedAccountForMenu.id,
                decodeMojibake(selectedAccountForMenu.companyName) || ''
              );
            }
          }}
          disabled={isGeneralAccountName(selectedAccountForMenu?.companyName)}
        >
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText primary={t('current.transactionDialog.collectPayment')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenMakePaymentModal(
                selectedAccountForMenu.id,
                decodeMojibake(selectedAccountForMenu.companyName) || ''
              );
            }
          }}
          disabled={isGeneralAccountName(selectedAccountForMenu?.companyName)}
        >
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('current.transactionDialog.makePayment')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenDebitModal(
                selectedAccountForMenu.id,
                decodeMojibake(selectedAccountForMenu.companyName) || ''
              );
            }
          }}
          disabled={isGeneralAccountName(selectedAccountForMenu?.companyName)}
        >
          <ListItemIcon>
            <ReceiptIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary={t('current.transactionDialog.debitAccount')} />
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenShippingAddressModal(selectedAccountForMenu as AccountDetails, e as any);
            }
          }}
          disabled={isGeneralAccountName(selectedAccountForMenu?.companyName)}
        >
          <ListItemIcon>
            <LocalShippingIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('current.actions.addShippingAddress')} />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handlePrintAccount(selectedAccountForMenu);
            }
          }}
        >
          <ListItemIcon>
            <PrintIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText primary={t('current.actions.print')} />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenEditAccountModal(selectedAccountForMenu);
            }
          }}
          disabled={isGeneralAccountName(selectedAccountForMenu?.companyName)}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('current.actions.edit')} />
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setActionsMenuAnchorEl(null);
            if (selectedAccountForMenu) {
              handleOpenDetailModal(selectedAccountForMenu as AccountDetails);
            }
          }}
        >
          <ListItemIcon>
            <InfoIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('current.actions.currentDetails')} />
        </MenuItem>
      </Menu>

      {/* Cari Grubu Modal */}
      <AccountGroupModal 
        open={isGroupModalOpen}
        onClose={handleCloseGroupModal}
        isManager={isManager}
        branchId={isManager ? (managerBranchId || undefined) : undefined}
      />

      {/* Yeni Cari Ekle / Düzenle Modal */}
      <AddAccount
        open={isAddAccountModalOpen}
        onClose={handleCloseAddAccountModal}
        editAccount={editingAccount}
        lockedBranchId={isManager ? managerBranchId : null}
      />

      {/* Cari Detay Modal */}
      <AccountDetail
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        account={selectedAccount}
        groupName={selectedAccount ? getGroupName(selectedAccount.groupId) : undefined}
        companyId={companyId}
      />

      {/* ✅ Manager: Cari Rapor (hesap hareketleri) — admin rotası yerine fullscreen dialog */}
      {isManager && (
        <Dialog
          fullScreen
          open={!!managerReportAccountId}
          onClose={() => setManagerReportAccountId(null)}
        >
          {managerReportAccountId && (
            <AccountTransactions
              accountId={managerReportAccountId}
              lockedBranchId={managerBranchId}
              onBack={() => setManagerReportAccountId(null)}
            />
          )}
        </Dialog>
      )}

      {/* ✅ Manager: Cari Hareket Raporu — admin rotası yerine fullscreen dialog */}
      {isManager && (
        <Dialog
          fullScreen
          open={isManagerMovementsReportOpen}
          onClose={() => setIsManagerMovementsReportOpen(false)}
        >
          {isManagerMovementsReportOpen && (
            <AccountMovementsReport
              lockedBranchId={managerBranchId}
              onBack={() => setIsManagerMovementsReportOpen(false)}
            />
          )}
        </Dialog>
      )}

      {/* Pasif Cariler Modal */}
      <PassiveAccounts
        open={isPassiveAccountsModalOpen}
        onClose={handleClosePassiveAccountsModal}
        companyId={companyId}
        onAccountActivated={handleAccountActivated}
        branchFilterId={isManager ? managerBranchId : null}
      />

      {/* "Cari Hareket Girişi" Dialog modalı kaldırıldı; Tahsilat/Ödeme butonları artık doğrudan 3 nokta menüsünden açılıyor. */}

      {/* Tahsilat Yapma Modal */}
      <CollectPaymentModal
        open={isCollectPaymentModalOpen}
        onClose={handleCloseCollectPaymentModal}
        accountId={selectedAccount?.id || ''}
        accountName={selectedAccount?.companyName || ''}
        companyId={companyId}
        branchId={effectiveBranchId}
        branchLocked={isManager}
        onSuccess={() => {
          // Tahsilat başarılı - cari listesini güncelle
          if (companyId) {
            loadAccounts(companyId);
          }
        }}
      />

      {/* Ödeme Yapma Modal */}
      <MakePaymentModal
        open={isMakePaymentModalOpen}
        onClose={handleCloseMakePaymentModal}
        accountId={selectedAccount?.id || ''}
        accountName={selectedAccount?.companyName || ''}
        companyId={companyId}
        branchId={effectiveBranchId}
        branchLocked={isManager}
        onSuccess={() => {
          // Ödeme başarılı - cari listesini güncelle
          if (companyId) {
            loadAccounts(companyId);
          }
        }}
      />

      {/* Cariyi Borçlandır (Tahakkuk) Modal */}
      <DebitAccountModal
        open={isDebitModalOpen}
        onClose={handleCloseDebitModal}
        accountId={selectedAccount?.id || ''}
        accountName={selectedAccount?.companyName || ''}
        companyId={companyId}
        branchId={effectiveBranchId}
        branchLocked={isManager}
        onSuccess={() => {
          // Borçlandırma başarılı - cari listesini güncelle
          if (companyId) {
            loadAccounts(companyId);
          }
        }}
      />

      {/* Sevk Adresi Modal */}
      <ShippingAddressModal
        open={isShippingAddressModalOpen}
        onClose={handleCloseShippingAddressModal}
        accountId={selectedAccount?.id || null}
        companyId={companyId}
        accountName={selectedAccount?.companyName || ''}
      />

      {/* Tablo Ayarları Modal */}
      <AccountsTableSettings
        open={isTableSettingsModalOpen}
        onClose={handleCloseTableSettingsModal}
        onSave={handleUpdateTableSettings}
        currentColumns={localizedTableColumns}
      />

      {/* Account Transactions Modal */}
      <Modal
        open={isAccountTransactionDrawerOpen}
        onClose={() => setIsAccountTransactionDrawerOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {selectedAccount?.companyName} {t('current.actions.transactions')}
          </Typography>
          
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              onClick={() => {
                setIsAccountTransactionDrawerOpen(false);
                setIsAccountTransactionDrawerOpen(true);
              }}
            >
              {t('current.actions.makeTransaction')}
            </Button>
            
            <Button 
              variant="contained" 
              color="success"
              onClick={() => handleOpenCollectPaymentModal(selectedAccount?.id || '', selectedAccount?.companyName || '')}
            >
              {t('current.actions.collectFromCurrent')}
            </Button>
            
            {!isManager && (
            <Button 
              variant="contained" 
              color="secondary"
              onClick={() => navigate(`/satis?accountId=${selectedAccount?.id}`)}
            >
              {t('current.actions.makeSalesToCurrent')}
            </Button>
            )}
          </Stack>
        </Box>
      </Modal>

      {/* Cariden Cariye Para Transferi Modal */}
      <CashTransferModal
        open={isCashTransferModalOpen}
        onClose={handleCloseCashTransferModal}
        companyId={companyId || ''}
        initialTransferDirection="account_to_account"
        onSuccess={() => {
          // İşlem başarılı olduğunda tabloyu yenile
          if (companyId) {
            loadAccounts(companyId);
          }
        }}
      />

      {/* Toplu Cari Ekleme Modalı */}
      <BulkAddAccountModal 
        open={isBulkAddAccountModalOpen}
        onClose={handleCloseBulkAddAccountModal}
        onAccountsAdded={handleAccountsAdded}
      />

      {/* Bilgilendirme Modalı */}
      <Dialog
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'info.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2
          }}
        >
          <InfoOutlinedIcon />
          <Typography variant="h6" fontWeight="bold">
            {t('current.menu.information')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoOutlinedIcon color="info" />
                {t('current.modals.information.overviewTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('current.modals.information.overviewText')}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: 'primary.50', borderLeft: 3, borderColor: 'primary.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75 }}>
                      {t('current.modals.information.featureAddTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('current.modals.information.featureAddText')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: 'secondary.50', borderLeft: 3, borderColor: 'secondary.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75 }}>
                      {t('current.modals.information.featureOperationsTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('current.modals.information.featureOperationsText')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: 'success.50', borderLeft: 3, borderColor: 'success.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75 }}>
                      {t('current.modals.information.featureReportingTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('current.modals.information.featureReportingText')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Genel Bilgi */}
            <Paper elevation={2} sx={{ p: 2, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="info.main" gutterBottom>
                {t('current.modals.information.balanceCalculationTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('current.modals.information.balanceCalculationText')}
              </Typography>
            </Paper>

            {/* Cari Listesi Bakiyeleri */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" />
                {t('current.modals.information.currentListBalancesTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" icon={<AssessmentIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('current.modals.information.currentListBalancesSubtitle')}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('current.modals.information.currentListRule1')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.currentListRule2')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.currentListRule3')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.currentListRule4')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.currentListRule5')}
                </Typography>
              </Alert>
            </Box>

            {/* Cari Hareket Detayı */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="secondary" />
                {t('current.modals.information.movementBalancesTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('current.modals.information.movementBalancesSubtitle')}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('current.modals.information.movementRule1')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.movementRule2')}
                </Typography>
                <Typography variant="body2">
                  {t('current.modals.information.movementRule3')}
                </Typography>
              </Alert>
            </Box>

            {/* Önemli Notlar */}
            <Paper elevation={2} sx={{ p: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="warning.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon />
                {t('current.modals.information.importantNotesTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('current.modals.information.note1')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('current.modals.information.note2')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('current.modals.information.note3')}
              </Typography>
            </Paper>

            {/* Nasıl Kullanılır */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAltIcon color="primary" />
                {t('current.modals.information.howToUseTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Chip label="1" size="small" color="primary" sx={{ minWidth: 28 }} />
                  <Typography variant="body2">
                    {t('current.modals.information.howToUseStep1')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Chip label="2" size="small" color="primary" sx={{ minWidth: 28 }} />
                  <Typography variant="body2">
                    {t('current.modals.information.howToUseStep2')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Chip label="3" size="small" color="primary" sx={{ minWidth: 28 }} />
                  <Typography variant="body2">
                    {t('current.modals.information.howToUseStep3')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setIsInfoModalOpen(false)}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
          >
            {t('current.modals.information.understood')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Accounts; 